import React, { useEffect, useMemo, useState } from "react";
import api from "../api/http";
import {
  FaBuilding,
  FaUsers,
  FaClock,
  FaDraftingCompass,
  FaTools,
  FaClipboardCheck,
  FaArrowRight,
} from "react-icons/fa";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

/* API CONFIG */
const API_PREFIX = "/api";
const baseURL = api?.defaults?.baseURL || "";

const baseHasApi =
  typeof baseURL === "string" && /\/api\/?$/i.test(baseURL);

const apiPath = (p) => {
  const clean = p.startsWith("/") ? p : `/${p}`;
  return baseHasApi ? clean : `${API_PREFIX}${clean}`;
};

/** Build absolute URL for images */
const apiOrigin = (() => {
  if (!baseURL) return "";
  try {
    const u = new URL(baseURL);
    let path = (u.pathname || "").replace(/\/+$/, "");
    if (path.toLowerCase().endsWith("/api")) {
      path = path.slice(0, -4);
    }
    return `${u.protocol}//${u.host}${path}`;
  } catch {
    return "";
  }
})();

const absUrl = (p) => {
  if (!p || typeof p !== "string") return "";
  if (p.startsWith("http")) return p;
  if (p.startsWith("/images/")) return p;

  let s = p.replace(/^\/+/, "");

  // Common Laravel cases
  if (s.startsWith("storage/")) {
  } else if (s.startsWith("public/")) {
    // Storage
    s = `storage/${s.slice("public/".length)}`;
  } else if (s.startsWith("uploads/") || s.startsWith("projects/") || s.startsWith("images/")) {
    s = `storage/${s}`;
  } else if (/^[A-Za-z0-9._-]+\.(png|jpe?g|webp|avif|gif)$/i.test(s)) {
    s = `storage/${s}`;
  }

  return apiOrigin ? `${apiOrigin}/${s}` : `/${s}`;
};

/* fallback cards */
const fallbackHomeProjects = [];

/* Normalizers */
function normalizeProject(p) {
  const title = p.title || p.name || "Untitled Project";
  const blurb = p.blurb || p.description || "";
  const owner =
    p.owner ||
    p.owner_name ||
    (p.client && (p.client.name || p.client)) ||
    "—";
  const address = p.address || p.location || "—";
  const price =
    p.price ||
    p.contract_value ||
    (typeof p.value === "number" ? `LKR ${p.value.toLocaleString("en-LK")}` : p.value) ||
    "—";

  // MAIN IMAGE
  const cover =
    absUrl(p.img) ||
    absUrl(p.image) ||
    absUrl(p.image_url) ||
    absUrl(p.cover) ||
    absUrl(p.cover_image) ||
    absUrl(p.thumbnail) ||
    "/images/Project1.jpeg";

  // GALLERY
  let images = [];
  if (Array.isArray(p.images)) {
    images = p.images;
  } else if (typeof p.images === "string" && p.images.trim()) {
    try {
      const parsed = JSON.parse(p.images);
      if (Array.isArray(parsed)) {
        images = parsed;
      } else if (typeof parsed === "string") {
        images = parsed.split(",").map((s) => s.trim()).filter(Boolean);
      }
    } catch {
      images = p.images.split(",").map((s) => s.trim()).filter(Boolean);
    }
  }
  images = images.map((it) =>
    absUrl(typeof it === "string" ? it : it?.url || it?.path || it?.src || it?.image || "")
  );
  if (!images.length) images = [cover];

  return { id: p.id || p.uuid || title, title, blurb, owner, address, price, cover, images };
}

/* Backend fetch helpers */
async function fetchRecentCompleted(limit = 3) {
  const candidates = [
    apiPath(`/projects?status=completed&per_page=${limit}&sort=updated_at&order=desc`),
    apiPath(`/projects?status=completed&limit=${limit}&sort=updated_at&order=desc`),
    apiPath(`/projects?status=completed&_limit=${limit}&_sort=updated_at&_order=desc`),
  ];

  for (const url of candidates) {
    try {
      const { data } = await api.get(url);
      const arr = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      if (arr.length) return arr.map(normalizeProject);
    } catch (e) {}
  }
  return [];
}

async function fetchCompletedCount() {
  const countUrls = [apiPath(`/projects/count?status=completed`)];

  for (const url of countUrls) {
    try {
      const { data } = await api.get(url);
      if (typeof data === "number") return data;
      if (typeof data?.count === "number") return data.count;
      if (typeof data?.total === "number") return data.total;
    } catch (e) {}
  }

  // Fallback
  const metaUrls = [apiPath(`/projects?status=completed&per_page=1`)];
  for (const url of metaUrls) {
    try {
      const { data } = await api.get(url);
      if (typeof data?.meta?.total === "number") return data.meta.total;
      if (typeof data?.total === "number") return data.total;
      const arr = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      if (arr.length && arr.length < 1000) return arr.length;
    } catch (e) {}
  }

  return null;
}

/* Project Modal */
function ProjectModal({ project, onClose }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => setIdx(0), [project]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (!project?.images?.length) return;
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % project.images.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + project.images.length) % project.images.length);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, project]);

  if (!project) return null;

  const images =
    project.images?.length ? project.images : [project.cover || project.img];
  const goPrev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const goNext = () => setIdx((i) => (i + 1) % images.length);

  const hasOverflow = images.length > 5;
  const thumbBase = hasOverflow ? images.slice(0, 4) : images.slice(0, 5);
  const overflowCount = hasOverflow ? images.length - 4 : 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      aria-modal="true"
      role="dialog"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative w-full max-w-5xl bg-white rounded-lg shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-5 border-b">
          <div>
            <h3 className="text-3xl font-extrabold text-neutral-900">
              {project.title}
            </h3>
            <p className="text-sm text-neutral-600 mt-1">{project.blurb}</p>
          </div>

          {/* Red Close */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 inline-flex items-center justify-center w-8 h-6 rounded-sm bg-red-600 text-white hover:bg-red-700"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
              <path d="M18.3 5.7 12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7 4.3 4.3l6.3 6.3 6.3-6.3 1.4 1.4z" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 flex flex-col gap-6">
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="text-sm font-semibold text-neutral-500">Owner</div>
              <div className="col-span-2 text-neutral-900">{project.owner}</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="text-sm font-semibold text-neutral-500">Address</div>
              <div className="col-span-2 text-neutral-900">{project.address}</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="text-sm font-semibold text-neutral-500">Contract Value</div>
              <div className="col-span-2 text-neutral-900">{project.price}</div>
            </div>
          </div>

          {/* Image Viewer */}
          <div className="relative w-full h-[320px] bg-neutral-100 rounded-xl overflow-hidden flex items-center justify-center">
            {images.length > 1 && (
              <button
                onClick={goPrev}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white grid place-items-center hover:bg-black/70"
                aria-label="Previous image"
              >
                <FaChevronLeft className="w-5 h-5" />
              </button>
            )}

            <img
              src={images[idx]}
              alt={`${project.title} ${idx + 1}`}
              className="max-h-full max-w-full object-contain"
              loading="lazy"
              onError={(e) => (e.currentTarget.src = "/images/Project1.jpeg")}
            />

            {images.length > 1 && (
              <button
                onClick={goNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white grid place-items-center hover:bg-black/70"
                aria-label="Next image"
              >
                <FaChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Thumbnails */}
          <div className="grid grid-cols-5 gap-3">
            {thumbBase.map((src, i) => {
              const isActive = i === idx;
              return (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`relative rounded-lg overflow-hidden border ${
                    isActive ? "border-sky-600 ring-2 ring-sky-300" : "border-neutral-200"
                  }`}
                  aria-label={`Show image ${i + 1}`}
                >
                  <img
                    src={src}
                    alt={`${project.title} thumbnail ${i + 1}`}
                    className="w-full h-20 object-cover"
                    loading="lazy"
                    onError={(e) => (e.currentTarget.src = "/images/Project1.jpeg")}
                  />
                </button>
              );
            })}

            {hasOverflow && (
              <button
                onClick={() => setIdx(4)}
                className={`relative rounded-lg overflow-hidden border ${
                  idx >= 4 ? "border-sky-600 ring-2 ring-sky-300" : "border-neutral-200"
                }`}
                aria-label={`Show more images`}
              >
                <img
                  src={images[4]}
                  alt={`${project.title} thumbnail 5`}
                  className="w-full h-20 object-cover brightness-75"
                  loading="lazy"
                  onError={(e) => (e.currentTarget.src = "/images/Project1.jpeg")}
                />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="px-2 py-1 text-sm font-semibold bg-black/60 text-white rounded-md">
                    +{overflowCount}
                  </span>
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const [selected, setSelected] = useState(null);

  // Dynamic data
  const [recentProjects, setRecentProjects] = useState([]);
  const [completedCount, setCompletedCount] = useState(null);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [loadingCount, setLoadingCount] = useState(true);

  // Years Established
  const yearsEstablished = useMemo(() => {
    const START = { y: 2009, m: 9, d: 2 };
    const now = new Date();
    let years = now.getFullYear() - START.y;
    const anniversaryThisYear = new Date(now.getFullYear(), START.m, START.d);
    if (now < anniversaryThisYear) years -= 1;
    return Math.max(0, years);
  }, []);

  // lock body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = selected ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [selected]);

  const openProject = (i) =>
    setSelected((recentProjects.length ? recentProjects : fallbackHomeProjects)[i]);

  /* Fetch dynamic sections */
  useEffect(() => {
    let alive = true;

    (async () => {
      setLoadingRecent(true);
      const items = await fetchRecentCompleted(3);
      if (!alive) return;
      setRecentProjects(items);
      setLoadingRecent(false);
    })();

    (async () => {
      setLoadingCount(true);
      const c = await fetchCompletedCount();
      if (!alive) return;
      setCompletedCount(c);
      setLoadingCount(false);
    })();

    return () => {
      alive = false;
    };
  }, []);

  const projectsForCards =
    recentProjects.length ? recentProjects : fallbackHomeProjects;

  return (
    <main id="home">
      {/* HERO */}
      <section className="relative">
        {/* Background image */}
        <div
          className="h-[520px] md:h-[670px] bg-cover bg-center"
          style={{ backgroundImage: "url('/images/Home.png')" }}
        />

        {/* Full-image blur + tint overlay */}
        <div className="absolute inset-0 bg-black/55" />

        {/* Content */}
        <div className="absolute inset-0">
          <div className="max-w-7xl mx-auto h-full px-4 md:px-6 flex">
            <div className="flex-1 flex items-center">
              <div className="max-w-3xl text-white">
                {/* Company Name Image */}
                <div className="flex items-center gap-2 mb-2">
                  <div className="relative inline-block isolate rounded-xl p-2 shadow-lg">
                    {/* translucent + blurred background layer */}
                    <div className="pointer-events-none absolute inset-0 rounded-md bg-white/80 backdrop-blur-lg ring-1 ring-white/40" />

                    {/* logo stays sharp */}
                    <img
                      src="/images/Name2.png"
                      alt="PERA Engineering"
                      className="relative h-[3.75rem] md:h-[3.75rem] object-contain"
                    />
                  </div>
                </div>

                {/* White translucent panel behind the text + CTA */}
                <div 
                  className="mt-4 inline-block rounded-xl bg-white/5 backdrop-blur-md ring-1 ring-white/15 px-5 py-4 shadow-lg"
                  style={{ fontFamily: '"Monotype Corsiva", cursive' }}
                >
                  <p
                    className="md:text-2xl leading-relaxed text-white"
                    style={{ textShadow: "0 1px 2px rgba(0,0,0,.7)" }}
                  >
                    Built on{" "}
                    <span className="inline-flex flex-wrap items-baseline gap-1">
                      {/* P */}
                      <span className="inline-block rounded px-1.5 py-0.5">
                        <span
                          className="text-[#8d1b1e] font-bold"
                          style={{ fontSize: "1.3em", lineHeight: 1 }}
                        >
                          P
                        </span>
                      </span>
                      <span className="text-white font-bold">lanning,</span>{" "}
                      {/* E */}
                      <span className="inline-block rounded px-1.5 py-0.5">
                        <span
                          className="text-[#8d1b1e] font-bold"
                          style={{ fontSize: "1.3em", lineHeight: 1 }}
                        >
                          E
                        </span>
                      </span>
                      <span className="text-white font-bold">valuation,</span>{" "}
                      {/* R */}
                      <span className="inline-block rounded px-1.5 py-0.5">
                        <span
                          className="text-[#8d1b1e] font-bold"
                          style={{ fontSize: "1.3em", lineHeight: 1 }}
                        >
                          R
                        </span>
                      </span>
                      <span className="text-white font-bold">esearch,</span>{" "}
                      <span className="text-white">and</span>{" "}
                      {/* A */}
                      <span className="inline-block rounded px-1.5 py-0.5">
                        <span
                          className="text-[#8d1b1e] font-bold"
                          style={{ fontSize: "1.3em", lineHeight: 1 }}
                        >
                          A
                        </span>
                      </span>
                      <span className="text-white font-bold">nalysis</span>
                    </span>{" "}
                    to deliver lasting construction excellence.
                  </p>
                </div>

                <a
                  href="#projects"
                  className="inline-block mt-6 px-6 py-3 rounded bg-cyan-400 font-semibold text-black hover:bg-cyan-300"
                >
                  VIEW OUR WORK
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES BAND */}
      <section id="services" className="relative">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white to-sky-50" />
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <p className="uppercase text-base sm:text-lg font-bold tracking-widest text-sky-600">
              Our Services
            </p>
            <h2 className="mt-2 text-2xl md:text-3xl font-extrabold">
              High-Quality Construction Solutions
              <br className="hidden md:block" /> for Residential & Industrial Projects
            </h2>
          </div>

          {/* service cards */}
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 items-stretch">
            {/* Card 1 – Design & Build */}
            <article className="rounded-2xl border bg-white shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all">
              <div className="p-7 flex flex-col w-full">
                <div className="mb-5">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-sky-50 ring-1 ring-sky-200 flex items-center justify-center">
                    <FaDraftingCompass className="text-sky-600 text-4xl md:text-5xl" />
                  </div>
                </div>
                <h3 className="text-xl font-bold">Design &amp; Build</h3>
                <p className="mt-2 text-base text-neutral-600">
                  We offer a single contract delivery model that takes your project from 
                  the initial concept stage all the way to IFC (Issued for Construction) drawings.
                </p>
                <ul className="mt-3 text-sm text-neutral-700 list-disc pl-5 space-y-1">
                  <li>Buildings</li>
                  <li>Shopping Complexes</li>
                  <li>School Buildings</li>
                  <li>Churches</li>
                  <li>Hindu Temples</li>
                </ul>
                <div className="mt-auto pt-6 flex justify-center sm:justify-end">
                  <a
                    href="#contact"
                    className="inline-flex items-center gap-2 px-8 py-3 rounded-md bg-sky-600 text-sm text-white font-semibold tracking-wide shadow-md transition-all duration-300 hover:bg-sky-700 hover:shadow-lg"
                  >
                    LEARN MORE <FaArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </article>

            {/* Card 2 – Remodels & Renovations */}
            <article className="rounded-2xl border bg-white shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all h-full flex">
              <div className="p-7 flex flex-col w-full">
                <div className="mb-5">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-sky-50 ring-1 ring-sky-200 flex items-center justify-center">
                    <FaTools className="text-sky-600 text-4xl md:text-5xl" />
                  </div>
                </div>
                <h3 className="text-xl font-bold">Remodel &amp; Renovations</h3>
                <p className="mt-2 text-base text-neutral-600">
                  We handle complete upgrades, adaptive reuse, and modernisation 
                  projects to keep your facilities up to date with today’s needs. 
                  This service is especially suited for large-scale commercial 
                  buildings, schools, and residential complexes.
                </p>
                <p className="mt-2 font-semibold text-sky-700">
                  Project value: LKR 10M and above.
                </p>
                <div className="mt-auto pt-6 flex justify-center sm:justify-end">
                  <a
                    href="#contact"
                    className="inline-flex items-center gap-2 px-8 py-3 rounded-md bg-sky-600 text-sm text-white font-semibold tracking-wide shadow-md transition-all duration-300 hover:bg-sky-700 hover:shadow-lg"
                  >
                    LEARN MORE <FaArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </article>

            {/* Card 3 – Construction Consult */}
            <article className="rounded-2xl border bg-white shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all h-full flex">
              <div className="p-7 flex flex-col w-full">
                <div className="mb-5">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-sky-50 ring-1 ring-sky-200 flex items-center justify-center">
                    <FaClipboardCheck className="text-sky-600 text-4xl md:text-5xl" />
                  </div>
                </div>
                <h3 className="text-xl font-bold">Construction Consult</h3>
                <p className="mt-2 text-base text-neutral-600">
                  We provide professional advisory services that cover feasibility studies, 
                  cost estimating, value engineering, and scheduling. 
                  Our guidance is customized to help you optimise investments, improve efficiency, 
                  and stay fully compliant with both local and international standards.
                </p>
                <div className="mt-auto pt-6 flex justify-center sm:justify-end">
                  <a
                    href="#contact"
                    className="inline-flex items-center gap-2 px-8 py-3 rounded-md bg-sky-600 text-sm text-white font-semibold tracking-wide shadow-md transition-all duration-300 hover:bg-sky-700 hover:shadow-lg"
                  >
                    LEARN MORE <FaArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* WHO WE ARE STRIP */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20 grid gap-10 lg:grid-cols-2 items-center">
          {/* Images */}
          <div className="relative flex justify-center">
            <div className="absolute left-8 top-8 w-2/3 h-3/4 border-2 border-red-600 -z-10" />
            <img
              src="/images/Who.jpeg"
              alt="Modern building"
              className="w-2/3 rounded-xl shadow-lg"
            />
            <img
              src="/images/Who1.jpg"
              alt="Engineer at site"
              className="absolute right-0 bottom-[-80px] w-2/3 rounded-xl shadow-xl border-4 border-white"
            />
          </div>

          {/* Content */}
          <div className="flex flex-col justify-center">
            <p className="uppercase text-base sm:text-lg font-bold tracking-widest text-sky-600">
              Who We Are
            </p>
            <h2 className="mt-3 text-2xl sm:text-3xl md:text-4xl font-extrabold text-neutral-900 leading-snug">
              We Make Finest Architectural
              <br className="hidden sm:block" /> Design With Great Passion
            </h2>
            <p className="mt-4 text-sm sm:text-base text-neutral-700 leading-relaxed">
              <span className="font-bold">PERA Engineering</span> stands for
              <span className="inline-flex flex-wrap items-baseline gap-1">
                      {/* P */}
                      <span
                        className="text-[#2e3192] font-bold"
                        style={{
                          fontFamily: '"Monotype Corsiva", cursive',
                          fontSize: "1.3em",
                          lineHeight: "1",
                        }}
                      >
                        P
                      </span>
                      <span
                        className="text-black  font-bold"
                        style={{ fontFamily: '"Monotype Corsiva", cursive' }}
                      >
                        lanning,
                      </span>{" "}
                      {/* E */}
                      <span
                        className="text-[#8d1b1e] font-bold"
                        style={{
                          fontFamily: '"Monotype Corsiva", cursive',
                          fontSize: "1.3em",
                          lineHeight: "1",
                        }}
                      >
                        E
                      </span>
                      <span
                        className="text-black  font-bold"
                        style={{ fontFamily: '"Monotype Corsiva", cursive' }}
                      >
                        valuation,
                      </span>{" "}
                      {/* R */}
                      <span
                        className="text-[#2e3192] font-bold"
                        style={{
                          fontFamily: '"Monotype Corsiva", cursive',
                          fontSize: "1.3em",
                          lineHeight: "1",
                        }}
                      >
                        R
                      </span>
                      <span
                        className="text-black  font-bold"
                        style={{ fontFamily: '"Monotype Corsiva", cursive' }}
                      >
                        esearch,
                      </span>{" "}
                      {/* Normal "and" (no bold, no special font) */}
                      <span className="text-black " style={{ fontFamily: "inherit" }}>
                        and
                      </span>{" "}
                      {/* A */}
                      <span
                        className="text-[#8d1b1e] font-bold"
                        style={{
                          fontFamily: '"Monotype Corsiva", cursive',
                          fontSize: "1.3em",
                          lineHeight: "1",
                        }}
                      >
                        A
                      </span>
                      <span
                        className="text-black font-bold"
                        style={{ fontFamily: '"Monotype Corsiva", cursive' }}
                      >
                        nalysis
                      </span>
                    </span>{" "}

              values that guide every project we
              undertake. By combining innovation, engineering expertise, and modern design, we build spaces that
              inspire. From residential developments to large-scale infrastructure, we deliver with precision,
              safety, and lasting excellence.
            </p>

            {/* Features */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex items-center gap-3">
                <span className="text-red-600 text-4xl sm:text-5xl md:text-6xl">👷</span>
                <div>
                  <div className="font-bold text-base sm:text-lg">Professional Team</div>
                  <p className="text-xs sm:text-sm text-neutral-600">Skilled engineers & architects.</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-red-600 text-4xl sm:text-5xl md:text-6xl">🏗️</span>
                <div>
                  <div className="font-bold text-base sm:text-lg">Quality Service</div>
                  <p className="text-xs sm:text-sm text-neutral-600">ISO standards & safety builds.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats strip (Completed Projects uses DB count) */}
        <div className="grid grid-cols-1 sm:grid-cols-3 text-white">
          {/* Projects */}
          <div className="bg-cyan-400 py-6 sm:py-8 px-4 sm:px-0 flex items-center sm:justify-center gap-3 sm:gap-4">
            <FaBuilding className="text-4xl sm:text-6xl text-black" />
            <div className="text-left">
              <div className="uppercase text-lg sm:text-2xl tracking-wider text-black font-extrabold">
                Completed Projects
              </div>
              <div className="text-xl sm:text-2xl font-extrabold text-black">
                {loadingCount ? "…" : (completedCount ?? "—")}
              </div>
            </div>
          </div>

          {/* Employees */}
          <div className="bg-sky-600 py-6 sm:py-8 px-4 sm:px-0 flex items-center sm:justify-center gap-3 sm:gap-4">
            <FaUsers className="text-4xl sm:text-6xl text-white" />
            <div className="text-left">
              <div className="uppercase text-lg sm:text-2xl tracking-wider text-white font-extrabold">
                Experienced Staff
              </div>
              <div className="text-xl sm:text-2xl font-extrabold text-white">50+</div>
            </div>
          </div>

          {/* Established Years (auto) */}
          <div className="bg-neutral-900 py-6 sm:py-8 px-4 sm:px-0 flex items-center sm:justify-center gap-3 sm:gap-4">
            <FaClock className="text-4xl sm:text-6xl text-white" />
            <div className="text-left">
              <div className="uppercase text-lg sm:text-2xl tracking-wider text-white font-bold">
                Years Established
              </div>
              <div className="text-xl sm:text-2xl font-extrabold text-white">
                {yearsEstablished}+
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LATEST PROJECTS */}
      <section id="projects" className="bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <p className="uppercase text-base sm:text-lg font-bold tracking-widest text-sky-600 text-center">
            Our Projects
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900 text-center">
            Recently Completed Works
          </h2>

          {/* Cards – use API data if present; fallback to demo */}
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            {(loadingRecent ? Array.from({ length: 3 }) : projectsForCards).map((p, i) => {
              if (loadingRecent) {
                return (
                  <article key={`sk-${i}`} className="border mt-3 rounded-sm animate-pulse">
                    <div className="h-56 w-full bg-neutral-200" />
                    <div className="p-5 space-y-3">
                      <div className="h-5 bg-neutral-200 rounded w-2/3" />
                      <div className="h-4 bg-neutral-200 rounded w-full" />
                      <div className="h-8 bg-neutral-200 rounded w-1/3 ml-auto" />
                    </div>
                  </article>
                );
              }

              // Prefer DB 'img' first
              const cover = absUrl(p.img || p.cover || p.cover_url || p.image || p.image_url || p.thumbnail);
              const blurb = p.blurb || p.description || "";
              return (
<article key={p.id || i}>
  <div className="border mt-3 rounded-sm overflow-hidden">
    <img
      src={cover}
      alt={`${p.title}${p.owner ? ` – ${p.owner}` : ""}${p.address ? ` – ${p.address}` : ""}`}
      className="h-56 w-full object-cover"
      loading="lazy"
      decoding="async"
      onError={(e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = "/images/Project1.jpeg";
      }}
    />

    <div className="p-5">
      {/* Title + owner inline */}
      <h3 className="font-bold text-neutral-900">
        {p.title}
        {/* {p.owner && (
          <span className="ml-2 font-semibold text-neutral-700">
            · {p.owner}
          </span>
        )} */}
      </h3>

      {/* Owner — Address line */}
      {(p.owner || p.address) && (
        <p className="mt-1 text-sm text-neutral-500">
          {p.owner && <span className="font-medium">{p.owner}</span>}
          {p.owner && p.address ? " — " : ""}
          {p.address}
        </p>
      )}

      <p className="mt-2 text-sm text-neutral-600 line-clamp-3">{blurb}</p>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => openProject(i)}
          className="mt-4 inline-block px-4 py-2 text-xs tracking-wide rounded-sm border border-neutral-900 text-neutral-900 font-semibold hover:bg-neutral-900 hover:text-white"
        >
          VIEW PROJECT
        </button>
      </div>
    </div>
  </div>
</article>
              );
            })}
          </div>

          <div className="text-center mt-6">
            <a
              href="/projects"
              className="inline-block px-10 py-3 rounded-md bg-sky-600 text-white font-semibold tracking-wide shadow-md transition-all duration-300 hover:bg-sky-700 hover:shadow-lg"
            >
              VIEW ALL PROJECTS
            </a>
          </div>
        </div>
      </section>

      {/* Modal */}
      <ProjectModal
        project={
          selected &&
          (recentProjects.length ? recentProjects : fallbackHomeProjects).find(
            (x) => (x.id || x.title) === (selected.id || selected.title)
          )
        }
        onClose={() => setSelected(null)}
      />
    </main>
  );
}
